/**
 * The one parser behind both ways of adding terms to a list: the add row and
 * the paste dialog. A term typed and a term pasted are judged the same way.
 *
 * @module components/ui/term-list
 */

/** Longest term a list holds. */
export const MAX_TERM_LENGTH = 120;

export type TermStatus = "ok" | "duplicate" | "invalid";

export type ParsedTerm =
  | {
      /** The cleaned term, or the raw line when it could not be cleaned to one. */
      term: string;
      status: "ok";
      reason?: undefined;
    }
  | {
      term: string;
      status: Exclude<TermStatus, "ok">;
      /** Why the term is not `ok`, in a few words. */
      reason: string;
    };

/**
 * Tidies one line: bullets and numbering dropped, whitespace collapsed.
 *
 * @param line - A line as typed or pasted.
 * @returns The term, or an empty string when nothing is left.
 */
export function cleanTerm(line: string): string {
  return line
    .replace(/^\s*(?:[-*•]|\d+[.)])\s+/, "")
    .replace(/\s+/g, " ")
    .trim();
}

function judge(term: string, taken: Set<string>): ParsedTerm {
  if (term.length > MAX_TERM_LENGTH) {
    return { term, status: "invalid", reason: `Over ${String(MAX_TERM_LENGTH)} characters` };
  }
  if ([...term].some((character) => character.charCodeAt(0) < 32 || character === "\u007f")) {
    return { term, status: "invalid", reason: "Contains control characters" };
  }
  if (taken.has(term.toLowerCase())) {
    return { term, status: "duplicate", reason: "Already listed" };
  }
  taken.add(term.toLowerCase());
  return { term, status: "ok" };
}

/**
 * Parses text into terms, one per line, judged against what the list holds.
 *
 * A term is a duplicate of an existing one, or of an earlier line, ignoring
 * case; the first occurrence is the one that counts. Blank lines are dropped.
 *
 * @param text - What was typed or pasted.
 * @param existing - Terms already in the list.
 * @returns One entry per non-blank line, in order.
 */
export function parseTerms(text: string, existing: readonly string[] = []): ParsedTerm[] {
  const taken = new Set(existing.map((term) => term.toLowerCase()));
  return text
    .split(/\r?\n/)
    .map(cleanTerm)
    .filter((term) => term.length > 0)
    .map((term) => judge(term, taken));
}

/**
 * Judges a single typed term, which is one line of {@link parseTerms}.
 *
 * @param text - What was typed.
 * @param existing - Terms already in the list, not counting the one being edited.
 * @returns The judgement, or `null` when nothing was typed.
 */
export function parseTerm(text: string, existing: readonly string[] = []): ParsedTerm | null {
  const [first] = parseTerms(cleanTerm(text), existing);
  return first ?? null;
}
